import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close } from '@mui/icons-material';
import {
  useGetStoreCreditTransactionsQuery,
  useGetLoyaltyTransactionsQuery,
} from '../../services/customerApi';

const STORE_CREDIT_LABELS = {
  opening: 'Opening balance',
  return_credit: 'Credit note',
  redeemed: 'Used on bill',
  adjusted: 'Adjustment',
};

const POINTS_LABELS = {
  earned: 'Earned',
  redeemed: 'Redeemed',
  redeem_refund: 'Returned (credit note)',
  earn_reversal: 'Reversed (return)',
  expired: 'Expired',
  adjusted: 'Adjustment',
  signup_bonus: 'Signup bonus',
  birthday_bonus: 'Birthday bonus',
  referral_bonus: 'Referral bonus',
  referral_referee_bonus: 'Referral bonus',
  review_bonus: 'Review bonus',
};

const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const points = (v) => `${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })} pts`;
const when = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

// "CN/26-27/00028 · against LKS/26-27/01261" — the documents behind an entry.
export const walletReference = (t) => {
  if (t.credit_note_number) return `${t.credit_note_number}${t.order_number ? ` · against ${t.order_number}` : ''}`;
  if (t.order_number) return `Bill ${t.order_number}`;
  return t.notes || '';
};

function Entry({ label, reference, at, change, balance, positive }) {
  return (
    <Box sx={{ py: 1.1, borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography fontSize="0.82rem" fontWeight={700} color="#1E293B">{label}</Typography>
        {reference && <Typography fontSize="0.75rem" color="#334155" sx={{ wordBreak: 'break-word' }}>{reference}</Typography>}
        <Typography fontSize="0.7rem" color="#94A3B8">{at}</Typography>
      </Box>
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Typography fontSize="0.84rem" fontWeight={800} color={positive ? '#16A34A' : '#DC2626'}>{change}</Typography>
        <Typography fontSize="0.7rem" color="#64748B">bal {balance}</Typography>
      </Box>
    </Box>
  );
}

function Ledger({ useLedgerQuery, customerId, enabled, renderRow, emptyText }) {
  const [page, setPage] = useState(1);
  const { data, isFetching, isError, refetch } = useLedgerQuery(
    { id: customerId, page },
    { skip: !enabled || !customerId, refetchOnMountOrArgChange: true },
  );
  const rows = data?.data || [];
  const meta = data?.meta || {};

  if (isError) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography fontSize="0.82rem" color="#DC2626">Couldn't load the history.</Typography>
        <Button size="small" onClick={() => refetch()} sx={{ textTransform: 'none' }}>Retry</Button>
      </Box>
    );
  }
  if (isFetching && rows.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>;
  }
  if (rows.length === 0) {
    return <Typography fontSize="0.82rem" color="#64748B" sx={{ py: 3, textAlign: 'center' }}>{emptyText}</Typography>;
  }
  return (
    <>
      {rows.map(renderRow)}
      {meta.total_pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1.5 }}>
          <Button size="small" disabled={page <= 1 || isFetching} onClick={() => setPage(page - 1)} sx={{ textTransform: 'none' }}>Newer</Button>
          <Typography fontSize="0.72rem" color="#64748B">Page {page} of {meta.total_pages}</Typography>
          <Button size="small" disabled={page >= meta.total_pages || isFetching} onClick={() => setPage(page + 1)} sx={{ textTransform: 'none' }}>Older</Button>
        </Box>
      )}
    </>
  );
}

// Read-only passbooks for the customer at the counter: where store credit and
// loyalty points came from and went, each entry naming its bill / credit note.
export default function CustomerWalletHistoryDialog({ open, customer, onClose, showPoints = true }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [tab, setTab] = useState('store_credit');
  const id = customer?.id;

  return (
    <Dialog open={open && !!id} onClose={onClose} fullScreen={fullScreen} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : '12px' } }}>
      <DialogTitle sx={{ pb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography fontSize="1rem" fontWeight={800} color="#1E293B">Wallet History</Typography>
          <Typography fontSize="0.76rem" color="#64748B">{[customer?.name, customer?.phone_number].filter(Boolean).join(' · ')}</Typography>
        </Box>
        <IconButton size="small" aria-label="Close" onClick={onClose} sx={{ color: '#94A3B8', borderRadius: '8px' }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="fullWidth" sx={{ px: 2, borderBottom: '1px solid #E2E8F0' }}>
        <Tab value="store_credit" label="Store Credit" sx={{ textTransform: 'none', fontWeight: 700 }} />
        {showPoints && <Tab value="points" label="Loyalty Points" sx={{ textTransform: 'none', fontWeight: 700 }} />}
      </Tabs>
      <DialogContent sx={{ pt: 1 }}>
        {tab === 'store_credit' && (
          <Ledger
            useLedgerQuery={useGetStoreCreditTransactionsQuery} customerId={id} enabled={open}
            emptyText="No store credit activity yet."
            renderRow={(t) => {
              const amt = Number(t.amount || 0);
              return (
                <Entry key={t.id} label={STORE_CREDIT_LABELS[t.transaction_type] || t.transaction_type}
                  reference={walletReference(t)} at={[when(t.created_at), t.store_name].filter(Boolean).join(' · ')}
                  change={`${amt >= 0 ? '+' : '-'}${money(Math.abs(amt))}`} balance={money(t.balance_after)} positive={amt >= 0} />
              );
            }}
          />
        )}
        {tab === 'points' && (
          <Ledger
            useLedgerQuery={useGetLoyaltyTransactionsQuery} customerId={id} enabled={open}
            emptyText="No loyalty points activity yet."
            renderRow={(t) => {
              const p = Number(t.points || 0);
              return (
                <Entry key={t.id} label={POINTS_LABELS[t.transaction_type] || t.transaction_type}
                  reference={walletReference(t)} at={when(t.created_at)}
                  change={`${p >= 0 ? '+' : '-'}${points(Math.abs(p))}`} balance={points(t.balance_after)} positive={p >= 0} />
              );
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
